import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { listPatientsApi, type PatientOut } from "@/portals/admin/api/adminPatientsApi";
import { adminQueryKeys } from "@/portals/admin/api/adminQueryKeys";
import { useCachedQuery } from "@/shared/hooks/useCachedQuery";
import { useAdminAuthStore } from "@/stores/adminAuthStore";

const PAGE_SIZE = 10;
const EMPTY: PatientOut[] = [];
const SEARCH_DEBOUNCE_MS = 350;

interface AdminPatientsContextValue {
  patients: PatientOut[];
  total: number;
  page: number;
  limit: number;
  search: string;
  loading: boolean;
  error: string | null;
  setPage: (page: number) => void;
  setSearch: (search: string) => void;
  refetch: () => void;
}

const AdminPatientsContext = createContext<AdminPatientsContextValue | null>(null);

export function AdminPatientsProvider({ children }: { children: ReactNode }) {
  const accessToken = useAdminAuthStore((s) => s.accessToken);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce the search box so we don't hit the backend on every keystroke.
  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [search]);

  // Cached per page + search: paging back, clearing a search or returning to
  // the Patients page shows that list at once, then refreshes it quietly.
  const query = useCachedQuery(
    accessToken ? `${adminQueryKeys.patients}${page}:${debouncedSearch}` : null,
    () => listPatientsApi({ page, limit: PAGE_SIZE, search: debouncedSearch || undefined }),
  );
  const patients = query.data?.data ?? EMPTY;
  const total = query.data?.total ?? 0;
  const loading = query.loading;
  const error = query.data === undefined && query.error ? query.error || "Unable to load patients." : null;

  // Any page may have changed — the shown one refetches, the rest reload on next view.
  const { cache } = query;
  const refetch = useCallback(() => cache.invalidate(adminQueryKeys.patients), [cache]);

  const value = useMemo(
    () => ({ patients, total, page, limit: PAGE_SIZE, search, loading, error, setPage, setSearch, refetch }),
    [patients, total, page, search, loading, error, refetch],
  );

  return <AdminPatientsContext.Provider value={value}>{children}</AdminPatientsContext.Provider>;
}

export function useAdminPatients() {
  const context = useContext(AdminPatientsContext);
  if (!context) {
    throw new Error("useAdminPatients must be used within AdminPatientsProvider");
  }
  return context;
}
